import { Info } from "lucide-react"
import { observer } from "mobx-react-lite"

import type { ChainConfig } from "@/domains/core"
import type { ILiquidityPool } from "@/domains/pools"
import type { Sort } from "@/domains/table-processor"
import { ShowWhenTrue } from "@/components/conditionals"
import { Pagination } from "@/components/pagination"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from "@/components/table"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/tooltip"
import { DisplaySortIcon } from "@/domains/pools/components/display-sort-icon"
import { ThDisplayName } from "@/domains/pools/components/table-headers/th-display-name"
import { ThTvlUsd } from "@/domains/pools/components/table-headers/th-tvl-usd"
import { paginate } from "@/domains/table-processor"

import { ManualClTableRow } from "./manual-cl-table-row"

/**
 * Manual-CL pool-list table. The column set differs from the legacy
 * "volume / fees" pool table because flagship CL LPs care about short-horizon
 * activity: 24-hour volume / fees, a rough APR annualisation and a
 * seven-day TVL sparkline. Every value is sourced from `ManualClPoolStore`
 * computeds that aggregate `PoolDayData` from the indexer — no additional
 * RPC, no approximations.
 *
 * The `colSpan` on the empty-state row matches the number of headers so we
 * do not re-introduce the latent cell-count drift from the legacy table.
 */
export const ManualClTable = observer(
  ({
    chainConfig,
    pools,
    sortBy,
    setSortBy,
    currentPageNumber,
    pageSize,
    setPageSize,
    setCurrentPageNumber,
    isCompact,
  }: {
    chainConfig: ChainConfig
    pools: Array<ILiquidityPool>
    sortBy: Sort
    setSortBy: (v: Sort) => void
    currentPageNumber: number
    setCurrentPageNumber: (v: number) => void
    pageSize: number
    setPageSize: (v: number) => void
    isCompact: boolean
  }) => {
    const paginated = paginate({
      currentPageNumber,
      pageSize,
      numberOfRows: pools.length,
    })

    const hasTableRows = pools.length > 0
    const COLUMN_COUNT = 7

    return (
      <TooltipProvider>
        <Table>
          <TableHead>
            <TableHeaderRow>
              <ThDisplayName setSortBy={setSortBy} sortBy={sortBy} name="Pair" colSpan={1} />
              <ThTvlUsd setSortBy={setSortBy} sortBy={sortBy} />
              <TableHeader
                className="text-right"
                classNameInner="justify-end"
                onClick={() =>
                  setSortBy({
                    key: "volumeUsd24h",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }
              >
                Volume (24h)
                <DisplaySortIcon sortBy={sortBy} column="volumeUsd24h" />
              </TableHeader>
              <TableHeader
                className="text-right"
                classNameInner="justify-end"
                onClick={() =>
                  setSortBy({
                    key: "feesUsd24h",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }
              >
                Fees (24h)
                <DisplaySortIcon sortBy={sortBy} column="feesUsd24h" />
              </TableHeader>
              <TableHeader
                className="text-right"
                classNameInner="justify-end"
                onClick={() =>
                  setSortBy({
                    key: "rangeApr7dPct",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }
              >
                <span className="inline-flex items-center gap-1">
                  7D range APR
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="text-t300 inline-flex cursor-help"
                        aria-label="7D range APR explanation"
                        // onClick bubbles to the header, which would flip
                        // the sort direction when the user's only intent
                        // is to read the tooltip. Stop propagation so the
                        // info icon acts as a pure help affordance.
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Info className="h-3 w-3" aria-hidden />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs">
                      <div className="text-t100 font-medium">How this is calculated</div>
                      <div className="text-t200 mt-1 leading-snug">
                        Assumes a position sized to the pool's 7-day high/low —
                        the same dashed reference lines the create-position
                        chart draws — and annualises the last 7 days of
                        fees against that range. Pools with no 7-day data
                        fall back to a symmetric ±2% window.
                      </div>
                      <div className="text-t300 mt-2 leading-snug">
                        Real APR depends on the range you actually pick and
                        on how long price stays inside it. Out-of-range
                        positions earn zero.
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </span>
                <DisplaySortIcon sortBy={sortBy} column="rangeApr7dPct" />
              </TableHeader>
              <TableHeader
                className="text-right"
                classNameInner="justify-end"
                isSortDisabled
              >
                7D TVL
              </TableHeader>
              <TableHeader className="w-fit" classNameInner="justify-center" isSortDisabled>
                Actions
              </TableHeader>
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            <ShowWhenTrue when={!hasTableRows}>
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="py-6 text-center">
                  No pools found
                </TableCell>
              </TableRow>
            </ShowWhenTrue>
            <ShowWhenTrue when={hasTableRows}>
              {pools
                .slice(
                  paginated.indexes.firstItemIndexOnPage,
                  paginated.indexes.lastItemIndexOnPage,
                )
                .map((pool: ILiquidityPool, index: number) => (
                  <ManualClTableRow
                    pool={pool}
                    chainConfig={chainConfig}
                    key={pool.address.toString()}
                    isCompact={isCompact}
                    isFirstItem={index === 0}
                  />
                ))}
            </ShowWhenTrue>
          </TableBody>
        </Table>

        <Pagination
          paginated={paginated}
          onChangeRowsPerPage={(rowsPerPage) => {
            setPageSize(rowsPerPage)
            setCurrentPageNumber(1)
          }}
          onClickPreviousPage={(previousPageNumber) => setCurrentPageNumber(previousPageNumber)}
          onClickNextPage={(nextPageNumber) => setCurrentPageNumber(nextPageNumber)}
        />
        <p className="mt-2 px-4 text-[11px] text-t300">
          * 7D range APR projects what a position sized to the pool's 7-day
          high/low would earn, using the last 7 days of fees (fees × 365/7 ÷
          TVL) scaled by range capital efficiency. It ignores gauge boosts,
          does not compound, and collapses to zero the moment price leaves
          the range — treat it as a ceiling, not a guaranteed yield.
        </p>
      </TooltipProvider>
    )
  },
)
